import { CalendarDaysIcon, ClipboardListIcon, PhoneCallIcon } from 'assets/icons';
import { INTERNAL_CATEGORY } from 'constants/eventCategories';
import { TASK_TYPE } from 'constants/taskTypes';
import { SUPER_ADMIN_ROLE } from 'helper/roles';
import {
  addDays,
  clockLabel,
  hourLabel,
  isoDay,
  shortDayLabel,
} from 'helper/timeLabels';
import joinDetails from 'helper/joinDetails';

// Only the most recent late tasks show: the others wait in « Mes tâches ».
export const LATE_SHOWN = 3;
export const TASK_GROUPS = ['late', 'fixed', 'none', 'done'];

// Twin of canEditAppointment in the api, which refuses the others
// (APPOINTMENT_FORBIDDEN): the owner, its creator or the agency manager (500).
// The day lists the invitations to a colleague's appointment too: theirs to
// tick. Without a known owner or viewer, the row stays the caller's own.
export const mayTickAppointment = (appointment, viewer) =>
  !appointment.masked &&
  (!appointment.ownerId ||
    !viewer?.id ||
    appointment.ownerId === viewer.id ||
    appointment.createdBy === viewer.id ||
    viewer.role === SUPER_ADMIN_ROLE);

/**
 * The rows of « Tâches du jour »: today's appointments, the tasks due today or
 * late, and the call list. Each tab is a source: « Rendez-vous » the planning,
 * « Appels » the call list, « Tâches » the tasks, which have a single kind (an
 * old Appel or Réunion task reads as a plain task). A callback still in the
 * call list has no row of its own.
 *
 * `doneNow` holds the rows ticked a moment ago, shown done before the api
 * answers. `labels` gives the texts: `text(key, values)` (home.*),
 * `categoryTitle(category)` and `typeLabel(type)`. Without `showCallList`
 * (a role that does not prospect), the call list is ignored: no row of its
 * own, and its callbacks keep their task rows instead of vanishing.
 * `viewer` ({ id, role }) locks the box of an appointment they may not edit.
 */
export function buildHomeItems(data, { now, doneNow = {}, labels, showCallList = true, viewer }) {
  const { text, categoryTitle, typeLabel } = labels;
  const today = isoDay(now);
  const yesterday = isoDay(addDays(now, -1));
  const shown = (key, settled) => (key in doneNow ? doneNow[key] : settled);
  const items = [];

  for (const appointment of data.appointments) {
    const key = `a-${appointment.id}`;
    const settled = appointment.status === 'done';
    const done = shown(key, settled);
    const start = new Date(appointment.startsAt);
    const internal = appointment.category === INTERNAL_CATEGORY;
    const external = Boolean(appointment.externalLink);
    const tickable = !external && mayTickAppointment(appointment, viewer);
    items.push({
      key,
      kind: 'appointment',
      appointmentId: appointment.id,
      externalLink: appointment.externalLink,
      tab: 'appointments',
      group: settled ? 'done' : 'fixed',
      sort: start.getTime(),
      icon: CalendarDaysIcon,
      settled,
      done,
      passed: !done && start < now,
      // A Google event is ticked in Google Calendar, not here.
      toggleable: tickable,
      lockedLabel: tickable ? undefined : text(external ? 'tasks.externalCheck' : 'tasks.invitationCheck'),
      title: internal ? appointment.title : categoryTitle(appointment.category),
      subtitle: internal
        ? joinDetails([text('tasks.appointment'), appointment.description || appointment.location])
        : joinDetails([
            appointment.company?.name,
            appointment.contactName || appointment.company?.contactName,
            appointment.company?.city || appointment.location,
          ]),
      right: clockLabel(start),
    });
  }

  const calls = showCallList ? data.calls : null;
  const pendingCallbacks = new Set((calls?.pending ?? []).map((call) => call.id));
  for (const task of data.tasks) {
    if (task.callbackId && pendingCallbacks.has(task.callbackId)) continue;
    const key = `t-${task.id}`;
    const settled = Boolean(task.doneAt);
    const done = shown(key, settled);
    const overdue = task.dueOn < today;
    const due = new Date(`${task.dueOn}T${task.dueTime ?? '23:59'}`);
    let right = text('tasks.today');
    if (done) right = text('tasks.done');
    else if (overdue) right = task.dueOn === yesterday ? text('tasks.yesterday') : shortDayLabel(task.dueOn);
    else if (task.dueTime) right = task.dueTime.slice(0, 5).replace(/^0/, '');
    items.push({
      key,
      kind: 'task',
      taskIds: [task.id],
      companyId: task.company?.id,
      company: task.company?.name,
      contact: task.contactName || task.company?.contactName,
      tab: 'other',
      group: settled ? 'done' : overdue ? 'late' : task.dueTime ? 'fixed' : 'none',
      sort: due.getTime(),
      icon: ClipboardListIcon,
      settled,
      done,
      late: overdue && !done,
      passed: Boolean(task.dueTime) && !overdue && !done && due < now,
      toggleable: true,
      title: task.title,
      subtitle: done
        ? text('tasks.doneAt', { time: hourLabel(task.doneAt ? new Date(task.doneAt) : now) })
        : joinDetails([task.company?.name, task.contactName]) || typeLabel(TASK_TYPE),
      right,
    });
  }

  if (calls) {
    const made = calls.made.length;
    const total = made + calls.pending.length;
    if (total) {
      const settled = !calls.pending.length;
      items.push({
        key: 'call-list',
        kind: 'callList',
        tab: 'calls',
        group: settled ? 'done' : 'none',
        sort: 0,
        icon: PhoneCallIcon,
        settled,
        done: settled,
        toggleable: false,
        title: text('tasks.callList'),
        subtitle: text('tasks.callListProgress', { done: made, total }),
        right: text('tasks.today'),
      });
    }
  }

  return items;
}

// What is left to do: a row ticked a moment ago no longer counts.
export const countOpenItems = (items) => {
  const open = items.filter((item) => !item.done);
  const inTab = (tab) => open.filter((item) => item.tab === tab).length;
  return {
    all: open.length,
    late: open.filter((item) => item.late).length,
    appointments: inTab('appointments'),
    calls: inTab('calls'),
    other: inTab('other'),
  };
};

// The groups of a tab, in order: late tasks from the most recent, the others
// in the order of the day. Empty groups go.
export const groupHomeItems = (items, tab) => {
  const visible = items.filter((item) => tab === 'all' || item.tab === tab);
  return TASK_GROUPS.map((id) => ({
    id,
    rows: visible
      .filter((item) => item.group === id)
      .sort((a, b) => (id === 'late' ? b.sort - a.sort : a.sort - b.sort)),
  })).filter((group) => group.rows.length);
};

/**
 * What « Appels du jour » says: calls to qualify first, then an empty list,
 * a finished one, or what is left, the late callbacks and the next one.
 * `callbacks` counts the planned callbacks (the orange arcs of the ring);
 * `firstToQualify` is the oldest call to qualify, { phoneCallId, companyId }.
 */
// The prospection screen, on the sheet of a call to qualify when there is one.
export const prospectionTarget = (callToQualify) => {
  if (!callToQualify?.companyId) return '/prospection';
  const params = new URLSearchParams({ appeler: callToQualify.companyId });
  if (callToQualify.phoneCallId) params.set('phoneCallId', callToQualify.phoneCallId);
  return `/prospection?${params}`;
};

export const callsSummary = (calls, now) => {
  const done = calls.made.length;
  const total = done + calls.pending.length;
  const planned = calls.pending.filter((call) => call.kind !== 'new');
  const ring = { done, total, callbacks: planned.length };
  if (calls.toQualify) {
    return {
      ...ring,
      state: 'toQualify',
      count: calls.toQualify,
      firstToQualify: calls.firstToQualify ?? null,
    };
  }
  if (!total) return { ...ring, state: 'none' };
  if (!calls.pending.length) {
    const reached = calls.made.filter(
      (call) => call.result === 'answered' || call.result === 'callback',
    ).length;
    return { ...ring, state: 'finished', reached, booked: calls.appointmentsBooked };
  }
  const timed = planned
    .map((call) => ({ at: new Date(call.dueAt), name: call.company?.name }))
    .sort((a, b) => a.at - b.at);
  const next = timed.find((call) => call.at >= now);
  return {
    ...ring,
    state: 'ongoing',
    left: calls.pending.length,
    late: timed.filter((call) => call.at < now).length,
    next: next && { time: clockLabel(next.at), name: next.name },
  };
};
